import React, { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";

const GoogleAddressAutocomplete = ({
  onAddressSelect,
  initialValue,
  placeholder,
  required,
}) => {
  const [inputValue, setInputValue] = useState(initialValue || "");
  const [isLoading, setIsLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const [showManualOption, setShowManualOption] = useState(false);
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Helper function to extract number from any string
  const extractNumberFromString = (text) => {
    if (!text) return { number: "", name: text || "" };

    // Match pattern: number followed by space then the rest
    const match = text.match(/^(\d+)\s+(.+)$/);

    if (match) {
      return {
        number: match[1],
        name: match[2],
      };
    }

    return { number: "", name: text };
  };

  // Parse address components and extract number from ALL possible sources
  const parseAddressComponents = (
    addressComponents,
    formattedAddress,
    originalSearchQuery,
  ) => {
    let streetNumber = "";
    let route = "";
    let locality = "";
    let subLocality = "";
    let administrativeAreaLevel1 = "";
    let administrativeAreaLevel2 = "";
    let postalCode = "";
    let country = "";
    let premise = ""; // For named properties
    let plusCode = ""; // For plus codes

    addressComponents.forEach((component) => {
      const types = component.types;
      const longName = component.long_name;

      if (types.includes("street_number")) {
        streetNumber = longName;
      }
      if (types.includes("route")) {
        route = longName;
      }
      if (types.includes("locality")) {
        locality = longName;
      }
      if (
        types.includes("sublocality") ||
        types.includes("sublocality_level_1")
      ) {
        subLocality = longName;
      }
      if (types.includes("administrative_area_level_1")) {
        administrativeAreaLevel1 = longName;
      }
      if (types.includes("administrative_area_level_2")) {
        administrativeAreaLevel2 = longName;
      }
      if (types.includes("postal_code")) {
        postalCode = longName;
      }
      if (types.includes("country")) {
        country = longName;
      }
      if (types.includes("premise")) {
        premise = longName;
      }
      if (types.includes("plus_code")) {
        plusCode = longName;
      }
    });

    console.log("🔍 Raw address components:", {
      streetNumber,
      route,
      subLocality,
      locality,
      premise,
      plusCode,
      formattedAddress,
      originalSearchQuery,
    });

    // Build the street name from route or sublocality
    let streetName = "";
    if (route) {
      streetName = route;
    } else if (subLocality) {
      streetName = subLocality;
    }

    // Try to get street number from various sources
    let finalStreetNumber = streetNumber;
    let finalStreetName = streetName;

    // 1. First, try to extract number from the original search query
    if (!finalStreetNumber && originalSearchQuery) {
      const extracted = extractNumberFromString(originalSearchQuery);
      if (extracted.number) {
        finalStreetNumber = extracted.number;
        if (extracted.name && !finalStreetName) {
          finalStreetName = extracted.name;
        }
        console.log("✅ Extracted from original search query:", extracted);
      }
    }

    // 2. If still no number, try premise component
    if (!finalStreetNumber && premise) {
      const extracted = extractNumberFromString(premise);
      if (extracted.number) {
        finalStreetNumber = extracted.number;
        console.log("✅ Extracted from premise:", extracted);
      }
    }

    // 3. If still no number, try plus code
    if (!finalStreetNumber && plusCode) {
      const extracted = extractNumberFromString(plusCode);
      if (extracted.number) {
        finalStreetNumber = extracted.number;
        console.log("✅ Extracted from plus code:", extracted);
      }
    }

    // 4. If no street number but we have a street name, try to extract number from it
    if (!finalStreetNumber && finalStreetName) {
      const extracted = extractNumberFromString(finalStreetName);
      if (extracted.number) {
        finalStreetNumber = extracted.number;
        finalStreetName = extracted.name;
        console.log("✅ Extracted number from street name:", extracted);
      }
    }

    // 5. If still no street number, try to extract from the first part of formatted address
    if (!finalStreetNumber && formattedAddress) {
      const firstPart = formattedAddress.split(",")[0];
      const extracted = extractNumberFromString(firstPart);
      if (extracted.number) {
        finalStreetNumber = extracted.number;
        if (extracted.name && !finalStreetName) {
          finalStreetName = extracted.name;
        }
        console.log("✅ Extracted from formatted address:", extracted);
      }
    }

    // Build full address line 1
    let addressLine1Parts = [];
    if (finalStreetNumber) {
      addressLine1Parts.push(finalStreetNumber);
    }
    if (finalStreetName) {
      addressLine1Parts.push(finalStreetName);
    }

    let addressLine1 = addressLine1Parts.join(" ");

    // If addressLine1 is still empty, use the first part of formatted address
    if (!addressLine1.trim() && formattedAddress) {
      addressLine1 = formattedAddress.split(",")[0];
    }

    // City: Use locality first, then sublocality, then administrative_area_level_2
    let city = locality || subLocality || administrativeAreaLevel2 || "";

    // State/Province
    let state = administrativeAreaLevel1 || "";

    const result = {
      streetNumber: finalStreetNumber,
      streetName: finalStreetName,
      addressLine1: addressLine1,
      city: city,
      state: state,
      postalCode: postalCode,
      country: country,
    };

    console.log("📦 Final parsed address:", result);

    return result;
  };

  useEffect(() => {
    const initServices = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
          geocoder.current = new window.google.maps.Geocoder();
          setIsGoogleMapsReady(true);
        } catch (error) {
          console.error("Error initializing Google Maps services:", error);
        }
      }
    };

    initServices();
    const timer = setTimeout(initServices, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setInputValue(initialValue || "");
  }, [initialValue]);

  const getPlacePredictions = async (input) => {
    if (!input || input.length < 3 || !autocompleteService.current) {
      setPredictions([]);
      setShowManualOption(false);
      return;
    }

    setIsLoading(true);
    try {
      const request = {
        input: input,
        componentRestrictions: { country: "za" },
        types: ["address"],
      };

      autocompleteService.current.getPlacePredictions(
        request,
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions &&
            predictions.length > 0
          ) {
            setPredictions(predictions);
            setShowPredictions(true);
            setShowManualOption(false);
          } else {
            setPredictions([]);
            setShowManualOption(true);
            setShowPredictions(true);
          }
          setIsLoading(false);
        },
      );
    } catch (error) {
      console.error("Error fetching predictions:", error);
      setIsLoading(false);
      setPredictions([]);
      setShowManualOption(true);
      setShowPredictions(true);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowPredictions(true);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      getPlacePredictions(value);
    }, 500);
  };

  const geocodeManualAddress = async (address) => {
    if (!geocoder.current || !address.trim()) {
      toast.error("Please enter a valid address");
      return;
    }

    setIsLoading(true);
    toast.loading("Searching for address...", { id: "geocoding" });

    try {
      const response = await geocoder.current.geocode({ address });
      if (response.results && response.results[0]) {
        const addressComponents = response.results[0].address_components;
        const formattedAddress = response.results[0].formatted_address;

        const parsed = parseAddressComponents(
          addressComponents,
          formattedAddress,
          address,
        );

        const addressData = {
          fullAddress: formattedAddress,
          streetNumber: parsed.streetNumber,
          streetName: parsed.streetName,
          addressLine1: parsed.addressLine1,
          city: parsed.city,
          state: parsed.state,
          postalCode: parsed.postalCode,
          country: parsed.country || "South Africa",
        };

        onAddressSelect(addressData);
        toast.success("Address found! Please review the details below.", {
          id: "geocoding",
        });
        setShowPredictions(false);
      } else {
        toast.error(
          "Could not find this address. Please try:\n- Adding a city name\n- Checking the spelling\n- Using the map to select location",
          {
            id: "geocoding",
            duration: 5000,
          },
        );
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      toast.error(
        "Address not found. Please try:\n1. Add city and province\n2. Check spelling\n3. Use the map to pick location",
        {
          id: "geocoding",
          duration: 5000,
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPrediction = (prediction) => {
    const searchQuery = inputValue; // Save what the user typed
    setInputValue(prediction.description);
    setShowPredictions(false);
    setIsLoading(true);
    toast.loading("Getting address details...", { id: "prediction" });

    if (geocoder.current) {
      geocoder.current.geocode(
        { placeId: prediction.place_id },
        (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            const addressComponents = results[0].address_components;
            const formattedAddress = results[0].formatted_address;

            const parsed = parseAddressComponents(
              addressComponents,
              formattedAddress,
              searchQuery,
            );

            const addressData = {
              fullAddress: formattedAddress,
              streetNumber: parsed.streetNumber,
              streetName: parsed.streetName,
              addressLine1: parsed.addressLine1,
              city: parsed.city,
              state: parsed.state,
              postalCode: parsed.postalCode,
              country: parsed.country || "South Africa",
            };

            console.log("📤 Sending to Dashboard:", addressData);

            onAddressSelect(addressData);
            toast.success("Address found! Please review the details below.", {
              id: "prediction",
            });
          } else {
            toast.error(
              "Could not get address details. Please enter manually.",
              {
                id: "prediction",
              },
            );
          }
          setIsLoading(false);
        },
      );
    } else {
      setIsLoading(false);
      toast.error(
        "Geocoding service unavailable. Please enter address manually.",
      );
    }
  };

  const handleManualSearch = () => {
    if (inputValue.trim()) {
      geocodeManualAddress(inputValue);
      setShowPredictions(false);
    } else {
      toast.error("Please enter an address first");
    }
  };

  const handleUseMapInstead = () => {
    setShowPredictions(false);
    if (window.openMapPicker) {
      window.openMapPicker();
    } else {
      toast.info(
        "Please click the Map button to select your location on the map",
        {
          duration: 4000,
          icon: "🗺️",
        },
      );
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowPredictions(false);
    }, 200);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (predictions.length > 0) {
        handleSelectPrediction(predictions[0]);
      } else if (inputValue.trim()) {
        geocodeManualAddress(inputValue);
      }
      setShowPredictions(false);
    }
  };

  if (!isGoogleMapsReady) {
    return (
      <div className="input-icon-wrapper">
        <i className="fa-solid fa-map-pin input-icon"></i>
        <input
          type="text"
          value={inputValue}
          placeholder="Loading address search..."
          disabled
          className="address-input"
        />
        <div className="loading-indicator">
          <i className="fa-solid fa-circle-notch fa-spin"></i>
        </div>
      </div>
    );
  }

  return (
    <div className="google-address-autocomplete">
      <div className="input-icon-wrapper">
        <i className="fa-solid fa-map-pin input-icon"></i>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          placeholder={
            placeholder ||
            "Start typing your address (e.g., 10065 Nokayamatlala Road, Nokayamatlala)"
          }
          required={required}
          className="address-input"
        />
        {isLoading && (
          <div className="loading-indicator">
            <i className="fa-solid fa-circle-notch fa-spin"></i>
          </div>
        )}
        <i className="fa-solid fa-magnifying-glass search-icon-right"></i>
      </div>

      {showPredictions && (
        <div className="predictions-dropdown">
          {predictions.length > 0 ? (
            <>
              {predictions.map((prediction) => (
                <div
                  key={prediction.place_id}
                  className="prediction-item"
                  onClick={() => handleSelectPrediction(prediction)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <i className="fa-solid fa-map-pin prediction-icon"></i>
                  <span>{prediction.description}</span>
                </div>
              ))}
            </>
          ) : (
            <div className="no-results-section">
              {showManualOption && inputValue.length >= 3 && (
                <>
                  <div className="no-results-message">
                    <i className="fa-solid fa-circle-info"></i>
                    <span>No exact matches found for "{inputValue}"</span>
                  </div>
                  <div className="manual-options">
                    <button
                      className="manual-search-btn"
                      onClick={handleManualSearch}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <i className="fa-solid fa-magnifying-glass"></i>
                      Search for "{inputValue}" anyway
                    </button>
                    <button
                      className="map-picker-suggestion"
                      onClick={handleUseMapInstead}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <i className="fa-regular fa-map"></i>
                      Can't find your address? Pick from map
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleAddressAutocomplete;
